import { useEffect, useRef } from 'react'

/**
 * Event handlers grouped by type, holding a map of Node, event-handler pairs
 * @type {Object.<string, Map.<Node, EventListener>>}
 */
const eventHandlers = {}

/**
 * @param {string} eventType
 * @param {EventListener} handler
 * @param {Node} [container=document]
 */
export default function useEventHandler (
  eventType,
  handler,
  container = document
) {
  const handlerRef = useRef(handler)
  const onEventRef = useRef(onEvent)

  useEffect(updateHandler, [handler])
  useEffect(componentDidMount, [eventType, container])

  function componentDidMount () {
    addEventListener(container, eventType, onEventRef.current)
    return componentWillUnmount
  }

  function componentWillUnmount () {
    removeEventListener(container, eventType, onEventRef.current)
  }

  function updateHandler () {
    handlerRef.current = handler
  }

  function onEvent (e) {
    handlerRef.current(e)
  }
}

/**
 * @param {Node} container
 * @param {string} eventType
 * @param {EventListener} handler
 */
function addEventListener (container, type, handler) {
  let typeHandlers = eventHandlers[type]
  if (!typeHandlers) {
    typeHandlers = eventHandlers[type] = new Map()
  }
  if (!typeHandlers.has(container)) {
    const handlers = [handler]
    const listener = createListener(handlers)

    typeHandlers.set(container, { handlers, listener })
    container.addEventListener(type, listener)
  } else {
    const { handlers } = typeHandlers.get(container)
    if (handlers.indexOf(handler) === -1) {
      // Mutating handlers as they are referenced in closure
      handlers.push(handler)
    }
  }
}

/**
 * @param {Node} container
 * @param {string} eventType
 * @param {EventListener} handler
 */
function removeEventListener (container, type, handler) {
  const typeHandlers = eventHandlers[type]
  if (typeHandlers && typeHandlers.has(container)) {
    const { handlers, listener } = typeHandlers.get(container)
    const handlerIndex = handlers.indexOf(handler)
    if (handlerIndex !== -1) {
      // Mutating handlers as they are referenced in closure
      handlers.splice(handlerIndex, 1)
      if (!handlers.length) {
        // No handlers left, clear listener and reference to DOM element
        typeHandlers.delete(container)
        container.removeEventListener(type, listener)
      }
    }
  }
}

/**
 * @param {EventListener[]} handlers
 * @returns {EventListener}
 */
function createListener (handlers) {
  return function handleEvents (event) {
    const self = this
    handlers.forEach(handler => handler.call(self, event))
  }
}
